import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './Tabs.module.css';

/**
 * Tabs Component - Reutilizable y optimizado
 * 
 * @param {Array} items - Array de objetos con { label, icon, component, show, disabled }
 * @param {number} activeTab - Índice del tab activo
 * @param {Function} setActiveTab - Función para cambiar el tab activo
 * @param {string} className - Clase CSS adicional para el contenedor
 * @param {boolean} lazy - Si es true, solo renderiza el contenido del tab activo
 */
const Tabs = ({ 
  items = [], 
  activeTab = 0, 
  setActiveTab, 
  className = '',
  lazy = false 
}) => {
  // Filtrar tabs visibles y memoizar para optimización
  const visibleTabs = useMemo(() => {
    return items.filter((tab) => tab.show !== false);
  }, [items]);

  // Validar que activeTab esté en rango válido
  const safeActiveTab = useMemo(() => {
    if (activeTab < 0 || activeTab >= visibleTabs.length) {
      return 0;
    }
    return activeTab;
  }, [activeTab, visibleTabs.length]);

  // Handler optimizado con useCallback
  const handleTabClick = useCallback((index) => {
    if (setActiveTab && index !== safeActiveTab) {
      const tab = visibleTabs[index];
      // No cambiar si el tab está deshabilitado
      if (!tab?.disabled) {
        setActiveTab(index);
      }
    }
  }, [setActiveTab, safeActiveTab, visibleTabs]);

  // Manejar navegación con teclado
  const handleKeyDown = useCallback((event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTabClick(index);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      const nextIndex = (index + 1) % visibleTabs.length;
      handleTabClick(nextIndex);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const prevIndex = (index - 1 + visibleTabs.length) % visibleTabs.length;
      handleTabClick(prevIndex);
    }
  }, [handleTabClick, visibleTabs.length]);

  // Obtener el contenido del tab activo
  const activeContent = useMemo(() => {
    if (lazy) {
      return visibleTabs[safeActiveTab]?.component || null;
    }
    return visibleTabs.map((tab, index) => (
      <div
        key={index}
        className={`${styles.tabPanel} ${index === safeActiveTab ? styles.tabPanelActive : styles.tabPanelHidden}`}
        role="tabpanel"
        aria-labelledby={`tab-${index}`}
        aria-hidden={index !== safeActiveTab}
      >
        {tab.component}
      </div>
    ));
  }, [visibleTabs, safeActiveTab, lazy]);

  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.tabsContainer} ${className}`}>
      {/* Tab Navigation */}
      <div 
        className={styles.tabsList} 
        role="tablist"
        aria-label="Tabs navigation"
      >
        {visibleTabs.map((tab, index) => {
          const isActive = index === safeActiveTab;
          const isDisabled = tab.disabled === true;

          return (
            <button
              key={index}
              id={`tab-${index}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${index}`}
              aria-disabled={isDisabled}
              tabIndex={isActive ? 0 : -1}
              className={`${styles.tabItem} ${isActive ? styles.tabItemActive : ''} ${isDisabled ? styles.tabItemDisabled : ''}`}
              onClick={() => handleTabClick(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              disabled={isDisabled}
            >
              {tab.icon && (
                <span className={styles.tabIcon} aria-hidden="true">
                  {tab.icon}
                </span>
              )}
              <span className={styles.tabLabel}>{tab.label}</span>
              {isActive && (
                <span className={styles.tabIndicator} aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className={styles.tabsContent}>
        {activeContent}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      component: PropTypes.node.isRequired,
      show: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  activeTab: PropTypes.number,
  setActiveTab: PropTypes.func.isRequired,
  className: PropTypes.string,
  lazy: PropTypes.bool,
};

export default Tabs;

